import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { Element } from 'react-scroll'
import { ScanFeedPageSectionNames, TimelineItemTypes } from 'consts/timelineConsts'
import GrinScanTooltip from 'components/Patients/Timeline/V2/MiniTimeline/GrinScanTooltip'
import StackedCountGrinScanItem from 'components/common/icons/timelineV2/mini-timeline/StackedCountGrinScanItem'
import TimeBasedGrinScanItem from 'components/common/icons/timelineV2/mini-timeline/TimeBasedGrinScanItem'
import { trackEvent } from 'utils/analyticsUtils'
import useScanOralScore from '../../useScanOralScore'
import TimelineMarker from './TimelineMarker'
import MissedScanMarker from './MissedScanMarker'
import {
  COUNTED_MARKER_Z_INDEX,
  getStackMarkers,
  MARKER_FOOTPRINT,
  SELECTED_MARKER_Z_INDEX,
  STACK_OFFSET
} from './timeBasedLayout'
/**
 * How long the tooltip survives the pointer leaving the stack.
 *
 * It has to outlast the trip from the marker to the tooltip's own body, or the arrows in it can
 * never be reached.
 */
const CLOSE_DELAY_MS = 200

const useStyles = makeStyles(theme => ({
  stack: {
    position: 'absolute',
    left: '50%',
    width: MARKER_FOOTPRINT,
    transform: 'translateX(-50%)',
    cursor: 'pointer',
    // Only the markers themselves are a hover target. The box around them is taller than the circles
    // it holds, so neighbouring stacks' boxes overlap, and the one drawn later would answer for a
    // marker the doctor is pointing at in the one before it.
    pointerEvents: 'none',
    padding: 0,
    border: 'none',
    background: 'none',
    '&:focus-visible': {
      outline: '2px solid #3C52EF',
      outlineOffset: 2,
      borderRadius: 4
    }
  },
  marker: {
    position: 'absolute',
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: MARKER_FOOTPRINT,
    height: MARKER_FOOTPRINT,
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'auto'
    }
  },
  selectedMarker: {
    '& > *': {
      boxShadow: '0 0 0 3px #3C52EF',
      borderRadius: '100%'
    }
  }
}))

/**
 * One position on the axis, holding every scan that landed too close to the others to be drawn
 * apart from them.
 *
 * A stack of one is an ordinary marker with the scan tooltip behind it. A stack of several is a
 * single hover target showing one scan at a time: the markers inside it overlap, so each carrying
 * its own tooltip means two popovers open at once, which is what the doctor ends up reading instead
 * of either of them. The arrows in the tooltip page through the rest of the stack.
 */
const TimelineStack = ({ stack, depth = 0, selectedTimelineItemId, onSelect, analyticsPayload = {} }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const anchorRef = useRef()
  const closeTimeout = useRef()
  // Clicking a marker focuses the stack, so clicking an arrow afterwards blurs it. Without knowing
  // the pointer is still on the tooltip, that blur closes the tooltip under the doctor's cursor.
  const isPointerInside = useRef(false)

  const { treatment } = useSelector(state => state.treatmentReducer)

  const markers = getStackMarkers(stack, selectedTimelineItemId)
  const isStacked = stack.items.length > 1
  // Only scans can be paged through - a missed scan has nothing to show but the date it was due,
  // which its own marker already says. In axis order, so the next arrow and a press of Down both
  // move the way the eye does: to a newer scan, further down the column.
  const scanItems = useMemo(() => stack.items.filter(item => item.type !== TimelineItemTypes.MissedScan), [stack.items])
  /** The card opens on the stack's most recent scan and pages back from there. */
  const newestScanPosition = Math.max(scanItems.length - 1, 0)

  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState(newestScanPosition)

  const currentItem = scanItems[Math.min(position, scanItems.length - 1)]
  const oralScores = useScanOralScore({ oralScores: currentItem?.payload?.oralScores })

  useEffect(() => () => clearTimeout(closeTimeout.current), [])

  const handleOpen = useCallback(() => {
    clearTimeout(closeTimeout.current)

    if (isOpen) {
      return
    }
    setIsOpen(true)
    // Reset here rather than on close: a pointer that comes back mid-fade keeps its place, and the
    // page a closed stack happens to be left on never outlives it.
    setPosition(newestScanPosition)
    trackEvent('Mini timeline - stack opened', { ...analyticsPayload, stackSize: stack.items.length })
  }, [analyticsPayload, isOpen, newestScanPosition, stack.items.length])

  const handleClose = useCallback(() => {
    // Delayed rather than immediate: the pointer has to cross the gap between the marker and the
    // tooltip to reach the arrows, and that counts as leaving the stack.
    clearTimeout(closeTimeout.current)
    closeTimeout.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS)
  }, [])

  const handlePointerEnter = useCallback(() => {
    isPointerInside.current = true
    handleOpen()
  }, [handleOpen])

  const handlePointerLeave = useCallback(() => {
    isPointerInside.current = false
    handleClose()
  }, [handleClose])

  const handleBlur = useCallback(() => {
    if (!isPointerInside.current) {
      handleClose()
    }
  }, [handleClose])

  const handlePage = useCallback(
    step => () => {
      // A stack of only missed scans has nothing to page, and would otherwise report a move.
      if (scanItems.length < 2) {
        return
      }

      const next = Math.min(Math.max(position + step, 0), scanItems.length - 1)
      if (next === position) {
        return
      }

      setPosition(next)
      trackEvent('Mini timeline - stack paged', {
        ...analyticsPayload,
        stackSize: stack.items.length,
        direction: step > 0 ? 'next' : 'previous',
        position: next + 1
      })
    },
    [analyticsPayload, position, scanItems.length, stack.items.length]
  )

  const handleKeyDown = useCallback(
    e => {
      // The arrows live in a portal, so they are not next in the tab order and tabbing to them would
      // blur the stack and unmount them. Paging from the stack itself is the keyboard's way in.
      const step = { ArrowUp: -1, ArrowLeft: -1, ArrowDown: 1, ArrowRight: 1 }[e.key]
      if (step) {
        // Stopped as well as prevented: the scan feed listens for the arrow keys too, and would
        // otherwise page the feed behind the stack at the same time.
        e.preventDefault()
        e.stopPropagation()
        handlePage(step)()
      }
    },
    [handlePage]
  )

  if (!isStacked) {
    return (
      <TimelineMarker
        item={stack.items[0]}
        offset={stack.offset}
        depth={depth}
        selectedTimelineItemId={selectedTimelineItemId}
        onSelect={onSelect}
        analyticsPayload={analyticsPayload}
      />
    )
  }

  return (
    // A button rather than a div: a stack is the only way to reach the scans inside it, so it has
    // to be operable from the keyboard. Focus shows the tooltip as hover does.
    <button
      type="button"
      ref={anchorRef}
      className={classes.stack}
      aria-label={t('pages.patients.selectedPatient.timelineV2.miniTimeline.stackLabel', {
        count: stack.items.length,
        date: moment(stack.items[stack.items.length - 1].eventDate).format('MMM D, YYYY')
      })}
      style={{
        top: stack.offset,
        height: MARKER_FOOTPRINT + (markers.length - 1) * STACK_OFFSET
      }}
      onFocus={handleOpen}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onClick={() => currentItem && onSelect({ timelineItem: currentItem })}
    >
      {stack.items.map(item => (
        <Element id={item.id} key={`anchor-${item.id}`} />
      ))}
      {markers.map(({ item, offset, hiddenCount }, i) => {
        const isSelected = item.id === selectedTimelineItemId
        // A missed scan is not part of the paged sequence, so it keeps the tooltip that says when it
        // was due, and neither opens the scan card nor answers a click meant for a scan.
        const isMissed = !hiddenCount && item.type === TimelineItemTypes.MissedScan

        return (
          <div
            className={[classes.marker, isSelected ? classes.selectedMarker : ''].join(' ')}
            // Later is lower is newer, and the newest goes in front. The counted marker is the
            // exception: it leads the stack, so that order buries its figures under the marker
            // after it. The selected scan beats both, so its ring is never clipped by a neighbour.
            style={{
              top: offset - stack.offset,
              zIndex: isSelected ? SELECTED_MARKER_Z_INDEX : hiddenCount ? COUNTED_MARKER_Z_INDEX : i + 1
            }}
            key={`marker-${item.id}`}
            // On the marker rather than the stack: React dispatches enter and leave only below the
            // node the two elements share, and the popper - portaled, but still a child of the stack
            // in the React tree - makes that the stack itself. Handlers up there would miss every
            // move between the card and a marker, and the card would close under the pointer.
            onMouseEnter={isMissed ? undefined : handlePointerEnter}
            onMouseLeave={isMissed ? undefined : handlePointerLeave}
            onClick={isMissed ? e => e.stopPropagation() : undefined}
          >
            {hiddenCount ? (
              <StackedCountGrinScanItem count={hiddenCount} />
            ) : isMissed ? (
              <MissedScanMarker timelineItem={item} />
            ) : (
              <TimeBasedGrinScanItem
                trackStatus={item.payload?.trackStatus}
                alignerNumber={item.payload?.alignerNumber}
              />
            )}
          </div>
        )
      })}
      {!!currentItem && (
        <GrinScanTooltip
          anchorEl={anchorRef.current}
          isOpen={isOpen}
          date={currentItem.eventDate}
          scanNumber={currentItem.payload?.scanNumber}
          alignerNumber={currentItem.payload?.alignerNumber}
          totalAlignerNumber={treatment?.totalApplianceNumber}
          scans={currentItem.payload?.scans}
          scanType={currentItem.payload?.scanType}
          urgency={currentItem.payload?.urgency}
          trackStatus={currentItem.payload?.trackStatus}
          oralScores={oralScores}
          onTrackReasonsClick={() =>
            onSelect({ timelineItem: currentItem, section: ScanFeedPageSectionNames.ScanTracking })
          }
          stackPosition={Math.min(position, scanItems.length - 1)}
          stackTotal={scanItems.length}
          onPrevious={handlePage(-1)}
          onNext={handlePage(1)}
          onMouseEnter={handlePointerEnter}
          onMouseLeave={handlePointerLeave}
        />
      )}
    </button>
  )
}

export default React.memo(TimelineStack)
